import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import CategoryCard from '../components/blog/CategoryCard';
import { Container } from '../components/ui/Container';
import Spinner from '../components/ui/Spinner';
import SEO from '../components/seo/SEO';
import { useRegionalSeo } from '../hooks/useRegionalSeo';
import { useSeoExtras } from '../context/SiteSeoContext';
import { fetchCategories, fetchPopularCategories } from '../store/actions/categoryActions';
import { transformCategories } from '../utils/transformData';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const PageHeader = styled.div`
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    margin-bottom: 4rem;
  }
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 3.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.mutedForeground};
  max-width: 42rem;
  line-height: 1.7;
  
  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.25rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 2rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 2rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 2.25rem;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
  margin-bottom: 4rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.75rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
  
  @media (min-width: 1440px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
  }
  
  @media (min-width: 1920px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 3rem;
  }
`;

const AllCategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.75rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
  
  @media (min-width: 1440px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 2.5rem;
  }
  
  @media (min-width: 1920px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 3rem;
  }
`;

const CategoryLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const SimpleCategoryCard = styled.div`
  background-color: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: 0.875rem 1rem;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 1rem;
  height: 100%;
  
  @media (min-width: 768px) {
    padding: 1rem 1.25rem;
    gap: 1.25rem;
  }

  @media (min-width: 1024px) {
    padding: 1.125rem 1.375rem;
    gap: 1.35rem;
  }
  
  &:hover {
    box-shadow: ${props => props.theme.shadows.medium};
    transform: translateY(-0.125rem);
  }
`;

const SimpleCategoryThumb = styled.div`
  width: 5.5rem;
  height: 5.5rem;
  border-radius: 1rem;
  overflow: hidden;
  flex-shrink: 0;
  background: ${props => props.theme.colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 1px ${props => props.theme.colors.border};

  @media (min-width: 768px) {
    width: 6.5rem;
    height: 6.5rem;
    border-radius: 1.125rem;
  }

  @media (min-width: 1024px) {
    width: 7.25rem;
    height: 7.25rem;
  }

  @media (min-width: 1440px) {
    width: 8rem;
    height: 8rem;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s ease;
  }

  ${SimpleCategoryCard}:hover & img {
    transform: scale(1.05);
  }
`;

const SimpleCategoryPlaceholder = styled.span`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.75rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  color: ${props => props.theme.colors.mutedForeground};
  line-height: 1;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const SimpleCategoryText = styled.div`
  min-width: 0;
  flex: 1;
`;

const CategoryName = styled.h3`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.25rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin: 0 0 0.35rem;
  line-height: 1.25;
  
  @media (min-width: 1024px) {
    font-size: 1.375rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.5rem;
  }
`;

const CategoryCount = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin: 0;
  
  @media (min-width: 1024px) {
    font-size: 0.9375rem;
  }
`;

const Categories = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { language, apiRegion, getCategoryUrl } = useLanguage();
  // Use language from context (set based on region from URL)
  const activeLanguage = language;

  const { categories: categoriesFromStore, popularCategories: popularCategoriesFromStore, loading } = useSelector(state => state.category);

  const regionalSeo = useRegionalSeo('/categories');
  const seoExtras = useSeoExtras();

  useEffect(() => {
    dispatch(fetchCategories({ lang: activeLanguage, region: apiRegion }));
    dispatch(fetchPopularCategories(4, { lang: activeLanguage, region: apiRegion }));
  }, [dispatch, activeLanguage, apiRegion]);

  const allCategories = transformCategories(categoriesFromStore);
  const popularCategories = transformCategories(popularCategoriesFromStore);

  if (loading) {
    return (
      <PageWrapper>
        <Header />
        <Main>
          <Container>
            <Spinner />
          </Container>
        </Main>
        <Footer />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SEO
        title="All Categories"
        description="Explore our diverse range of topics and discover articles that match your interests."
        url={regionalSeo.pageUrl}
        language={regionalSeo.language}
        availableLanguages={regionalSeo.availableLanguages}
        alternateUrls={regionalSeo.alternateUrls}
        {...seoExtras}
      />
      <Header />
      <Main>
        <Container>
          <PageHeader>
            <PageTitle>All Categories</PageTitle>
            <PageDescription>
              Explore our diverse range of topics and discover articles that match your interests.
            </PageDescription>
          </PageHeader>

          {popularCategories.length > 0 && (
            <>
              <SectionTitle>{t('section.popularCategories')}</SectionTitle>
              <CategoriesGrid>
                {popularCategories.map((category) => (
                  <CategoryCard
                    key={category.slug || category.name}
                    name={category.name}
                    color={category.color}
                    imageUrl={category.imageUrl}
                    postCount={category.postCount || 0}
                  />
                ))}
              </CategoriesGrid>
            </>
          )}

          <SectionTitle>All Categories</SectionTitle>
          <AllCategoriesGrid>
            {allCategories.map((category) => (
              <CategoryLink key={category.slug || category.name} to={getCategoryUrl(category.slug || category.name.toLowerCase(), activeLanguage)}>
                <SimpleCategoryCard>
                  <SimpleCategoryThumb>
                    {category.imageUrl ? (
                      <img src={category.imageUrl} alt="" />
                    ) : (
                      <SimpleCategoryPlaceholder>
                        {(category.name || 'C').charAt(0).toUpperCase()}
                      </SimpleCategoryPlaceholder>
                    )}
                  </SimpleCategoryThumb>
                  <SimpleCategoryText>
                    <CategoryName>{category.name}</CategoryName>
                    <CategoryCount>{category.postCount || category.count || 0} articles</CategoryCount>
                  </SimpleCategoryText>
                </SimpleCategoryCard>
              </CategoryLink>
            ))}
          </AllCategoriesGrid>
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Categories;

